<template>
   <AppLayout>
      <template #main>
         <div
            v-for="(block, key) in simpleBlocks"
            :key="block.id"
         >
            <LeftSection
               v-if="key % 2 === 0"
               :header="block.title"
               :url="route('coaching.index')"
               :image-url="block.media.original_url"
            >
               <slot>
                  <section
                     class="sm:py-6 sm:space-y-6 "
                     v-html="block.subtitle"
                  />
               </slot>
            </LeftSection>

            <RightSection
               v-else
               :header="block.title"
               :with-background="true"
               :image-url="block.media.original_url"
            >
               <slot>
                  <section
                     aria-labelledby="information-heading"
                     class="py-6"
                     v-html="block.subtitle"
                  />
               </slot>
            </RightSection>
         </div>

         <div
            id="contact_form"
            class="mt-20 mx-auto max-w-2xl mb-24 sm:px-6 lg:max-w-7xl bg-secondary py-4 rounded p-4 sm:p-0"
         >
            <h2
               class="
            text-center
            font-bold
            text-3xl"
            >
               {{ $t('coaching.any_questions') }}
            </h2>
            <h2 class="text-center mt-6 font-semibold">
               {{ $t('coaching.any_questions_subtitle') }}
            </h2>
            <form
               class="mt-16"
               @submit.prevent="submitForm"
            >
               <div
                  class="flex flex-col space-y-3"
               >
                  <div class="grow flex flex-col space-y-1">
                     <InputLabel
                        class="font-semibold required"
                        :value="$t('contact.full_name')"
                     />
                     <InputText
                        size="small"
                        v-model="contactForm.name"
                     />
                     <InputError
                        :message="contactForm.errors.name"
                     />
                  </div>
                  <div class="grow flex flex-col space-y-1">
                     <InputLabel
                        class="font-semibold required"
                        :value="$t('contact.email')"
                     />
                     <InputText
                        size="small"
                        v-model="contactForm.email"
                        type="email"
                     />
                     <InputError
                        class="mt-2"
                        :message="contactForm.errors.email"
                     />
                  </div>
                  <div class="grow flex flex-col space-y-1">
                     <InputLabel
                        class="font-semibold required"
                        :value="$t('contact.phone_number')"
                     />
                     <PhoneInput v-model="contactForm.phone" />
                     <InputError
                        class="mt-2"
                        :message="contactForm.errors.phone"
                     />
                  </div>
                  <div class="grow flex flex-col space-y-1">
                     <InputLabel
                        class="font-semibold required"
                        :value="$t('contact.message')"
                     />
                     <Textarea
                        size="small"
                        v-model="contactForm.message"
                        rows="4"
                        class="w-full rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-primary-color sm:text-sm sm:leading-6"
                     />
                     <InputError
                        class="mt-2"
                        :message="contactForm.errors.message"
                     />
                  </div>
                  <div class="flex flex-col space-y-1">
                     <VueRecaptcha
                        ref="reCaptcha"
                        @verify="onVerifyCaptcha"
                        :sitekey="page.props.recaptcha_site_key"
                     />
                     <InputError
                        :message="
                           contactForm.errors.g_recaptcha_response
                        "
                     />
                  </div>
               </div>
               <div class="mt-8 flex justify-end">
                  <Button
                     size="small"
                     type="submit"
                     :loading="contactForm.processing"
                     :disabled="contactForm.processing"
                     class="w-full"
                  >
                     {{ $t('contact.lets_talk') }}
                  </Button>
               </div>
            </form>
         </div>

         <SponsorsSection />
      </template>
   </AppLayout>
</template>

<script setup>
   import AppLayout from '@/Layouts/AppLayout.vue';
   import LeftSection from '@/Components/Pages/Coaching/LeftSection.vue';
   import RightSection from '@/Components/Pages/Coaching/RightSection.vue';
   import InputLabel from '@/Components/Forms/InputLabel.vue';
   import InputError from '@/Components/Forms/InputError.vue';
   import { useForm, usePage } from '@inertiajs/vue3';
   import { isEmpty } from 'lodash';
   import { computed, ref } from 'vue';
   import { VueRecaptcha } from 'vue-recaptcha';
   import PhoneInput from '@/Components/Forms/PhoneInput.vue';
   import SponsorsSection from '@/Components/SponsorsSection.vue';

   import { Button, InputText, Textarea } from 'primevue';
   import { useToast } from 'primevue/usetoast';

   const reCaptcha = ref();

   const props = defineProps({
      pageBlocks: Object,
   });

   const simpleBlocks = computed(() => {
      return props.pageBlocks.simple.data;
   });

   const contactForm = useForm({
      name: null,
      email: null,
      phone: null,
      message: null,
      g_recaptcha_response: null,
   });

   const page = usePage();
   const toast = useToast();
   function submitForm () {
      contactForm.post(route('contact.store'), {
         preserveScroll: true,
         onSuccess: () => {
            contactForm.reset();
            reCaptcha.value.reset();
            const toastData = page.props.flash;
            if (!isEmpty(toastData)) {
               toast.add({
                  group: 'headless',
                  severity: 'custom',
                  type: toastData.type,
                  summary: toastData.title,
                  detail: toastData.message,
                  life: 3000,
               });
            }
         },
      });
   }

   const onVerifyCaptcha = (response) => {
      contactForm.g_recaptcha_response = response;
      contactForm.errors.g_recaptcha_response = null;
   };
</script>

<style scoped></style>
